import { useEffect, useImperativeHandle, useState } from 'react';
import Moment from 'moment';
import PilotagePilotWaitingHoursReasonTypeIdbRepository from '../../../../repositories/idb/PilotagePilotWaitingHoursReasonTypeIdbRepository';
import { TextArea } from "@progress/kendo-react-inputs";
import {
    FieldWrapper,
} from "@progress/kendo-react-form";
import {
    Hint,
    Label,
} from "@progress/kendo-react-labels";
import SelectOption from '../../../../components/layout/SelectOption';
import Spacer from '../../../../components/layout/Spacer';
import { isObjectNull } from '../../../../components/helpers/ObjectHelpers';
import { getDateDiffInMinutes } from '../../../../components/helpers/DateTimeHelpers';
import Slide from '../../../../components/layout/Slide';
import { PilotAssignmentErrorCodes } from '../../services/DispatcherActions';
import { useHasEditableChanged } from '../hooks/usePilotAssignmentStore';

export const TimeWaitingHoursCard = (
    {
        ref,
        initializeIsDirty,
        assignmentService
    }) => {

    useImperativeHandle(ref, () => ({
        onPilotageChanged() {
            initializeAsync();
        },
        onLocationChanged() {
            initializeAsync();
        },
        onArrivalTimeChanged() {
            initializeAsync();
        },
        onValidationChanged(codeStrings) {
        const hasReasonError = codeStrings.includes(PilotAssignmentErrorCodes.WaitingHourReasonIsMissing);
        const hasRemarkError = codeStrings.includes(PilotAssignmentErrorCodes.WaitingHourReasonRemarkIsEmpty);
        setHasValidationError(hasReasonError || hasRemarkError);
    }
    }));

    const [shouldShow, setShouldShow] = useState(false);
    const [reason, setReason] = useState({ id: 0, name: "Ikke valgt", isRemarkRequired: false });
    const [remark, setRemark] = useState("");
    const [hours, setHours] = useState("");
    const [reasonTypes, setReasonTypes] = useState([]);
    const [isEditable, setIsEditable] = useState(false);
    const [hasValidationError, setHasValidationError] = useState(false);
    const maxRemarkLength = 57;

    const hasEditableChanged = useHasEditableChanged();

    useEffect(() => {
        initializeAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initializeAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!hasEditableChanged) return;
        initializeAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasEditableChanged]);

    return (
        <Slide show={shouldShow}>
            <Spacer height={5} />
            <FieldWrapper>
                <Label className="k-form-label">
                    Ventetid
                </Label>
                <div className="k-form-field-wrap">
                    {hours} timer
                </div>
            </FieldWrapper>
            <Spacer height={10} />
            <FieldWrapper>
                <Label className="k-form-label">
                    Begrunnelse
                </Label>
                <div className="k-form-field-wrap">
                    <SelectOption
                        options={reasonTypes}
                        selectedOption={reason}
                        optionTextField="name"
                        title={`Endre ventetid begrunnelse`}
                        onCallback={onCallback}
                        disabled={!isEditable}
                        hasValidationError={hasValidationError}
                    />
                </div>
            </FieldWrapper>

            {
                reason.isRemarkRequired &&
                <FieldWrapper>
                    <Spacer height={10} />
                    <Label hasValidationError={hasValidationError && reason.isRemarkRequired}>
                        Merknad
                    </Label>
                    <div className="k-form-field-wrap">
                        <TextArea
                            disabled={!isEditable}
                            value={remark}
                            rows={2}
                            maxLength={maxRemarkLength}
                            onChange={onRemarkChangeAsync}
                            className="font-size-lg"
                            valid={!(hasValidationError && reason.isRemarkRequired)}
                            />
                        <Hint direction={'end'}>{remark.length} / {maxRemarkLength}</Hint>
                    </div>
                </FieldWrapper>
            }
        </Slide>
    )

    async function initializeAsync() {

        let show = false;
        let hours = 0;

        const pilotAssignment = assignmentService.assignment;
        setIsEditable(assignmentService.isEditable);

        if (pilotAssignment.isWastedTrip) {
            setShouldShow(false);
            return;
        }

        setRemark(pilotAssignment.waitingHoursReasonRemark);

        const startTime = pilotAssignment.locations[0].fromTime;
        const availableTime = getAvailableTimeForPilot(pilotAssignment);

        if (!isObjectNull(availableTime) && !isObjectNull(startTime)) {

            const deductableMinutesForFirstLeg = 60;
            let waitingMinutes = 0;

            const shipReadyForPilotageMoment = Moment(pilotAssignment.pilotage.pilotageDetail.fromTime);
            const availableMoment = Moment(availableTime);
            const startMoment = Moment(startTime);

            if (availableMoment >= shipReadyForPilotageMoment && startMoment > availableMoment) {
                waitingMinutes = getDateDiffInMinutes(availableMoment, startMoment);
            }
            else if (availableMoment < shipReadyForPilotageMoment && startMoment > shipReadyForPilotageMoment) {
                waitingMinutes = getDateDiffInMinutes(shipReadyForPilotageMoment, startMoment);
            }

            if (waitingMinutes > deductableMinutesForFirstLeg) {
                show = true;
                hours = (waitingMinutes / 60).toFixed(1);
            }
        }

        setHours(hours);
        setShouldShow(show);

        let reason = {};

        if (!isObjectNull(pilotAssignment.waitingHoursReasonType)) {

            const dto = await PilotagePilotWaitingHoursReasonTypeIdbRepository.getAsync(pilotAssignment.waitingHoursReasonType.pilotagePilotWaitingHoursReasonTypeId);
            reason = dto;
        }
        setReason(reason);

        initializeReasonTypesAsync(reason);
    }

    async function initializeReasonTypesAsync(reason) {
        const reasons = [];
        const dtos = await PilotagePilotWaitingHoursReasonTypeIdbRepository.getAllAsync();
        for (let i = 0; i < dtos.length; i++) {
            const dto = dtos[i];
            reasons.push({
                name: dto.name,
                selected: isObjectNull(reason) ? false : dto.systemName === reason.systemName,
                source: dto
            })
        }

        setReasonTypes(reasons);
    }

    async function onCallback(item) {
        const obj = item.source;
        setReason(obj);

        await assignmentService.onWaitingHourReasonAsync(obj, remark);

        initializeReasonTypesAsync(obj);
        initializeIsDirty();
    }

    async function onRemarkChangeAsync(e) {
        if (!isEditable) return;
        if(!reason.isRemarkRequired) return;

        setRemark(e.value);

        await assignmentService.onWaitingHourReasonAsync(reason, e.value);

        initializeIsDirty();
    }

    function getAvailableTimeForPilot(pilotAssignment) {

        if (isObjectNull(pilotAssignment.arrivalTime) && !pilotAssignment.isWastedTrip) {
            return pilotAssignment.pilotage.pilotageDetail.shipReadyForPilotageTime
        }

        return pilotAssignment.arrivalTime;
    }
}
